import { formatOrder } from "../utils/orderFormatting";
import { useOrderFlashing } from "../hooks/useOrderFlashing";
import { cn } from "../lib/utils";
import { OrderBookRows, type OrderBookDisplayRow } from "./OrderBookRows";
import { useLocalization } from "../lib/i18n";

type MarketCellProps = {
  teamCode: string;
  teamState: string;
  topBidPrice: number | null;
  topBidQuantity: number | null;
  topBidTraderCode: string | null;
  topBidParticipantType?: 'HUMAN' | 'VIRTUAL' | null;
  topBidParticipantDisplayName?: string | null;
  topBidOrderId: number | string | null;
  topAskPrice: number | null;
  topAskQuantity: number | null;
  topAskTraderCode: string | null;
  topAskParticipantType?: 'HUMAN' | 'VIRTUAL' | null;
  topAskParticipantDisplayName?: string | null;
  topAskOrderId: number | string | null;
  marketFloorPrice: number;
  isExpanded?: boolean;
  variant?: "default" | "tabletCard";
  onSelectOrder?: (payload: { teamCode: string; side: "BUY" | "SELL"; price?: number }) => void;
};

export function MarketCell({
  teamCode,
  teamState,
  topBidPrice,
  topBidQuantity,
  topBidTraderCode,
  topBidParticipantType,
  topBidParticipantDisplayName,
  topBidOrderId,
  topAskPrice,
  topAskQuantity,
  topAskTraderCode,
  topAskParticipantType,
  topAskParticipantDisplayName,
  topAskOrderId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  marketFloorPrice,
  isExpanded = false,
  variant = "default",
  onSelectOrder,
}: MarketCellProps) {
  const { t } = useLocalization();
  const isFlashing = useOrderFlashing({
    topBidOrderId,
    topBidPrice,
    topBidQuantity,
    topBidTraderCode,
    topAskOrderId,
    topAskPrice,
    topAskQuantity,
    topAskTraderCode,
  });

  const bid = formatOrder(topBidPrice, topBidQuantity, topBidTraderCode, "bid");
  const ask = formatOrder(topAskPrice, topAskQuantity, topAskTraderCode, "ask");
  const participantLabel = (display: string, type?: 'HUMAN' | 'VIRTUAL' | null, name?: string | null) =>
    type === "VIRTUAL" ? `${display ?? name ?? t("market.participant.virtual")} · ${t("market.participant.virtual")}` : display;
  const bidTraderDisplay = participantLabel(bid.traderDisplay, topBidParticipantType, topBidParticipantDisplayName);
  const askTraderDisplay = participantLabel(ask.traderDisplay, topAskParticipantType, topAskParticipantDisplayName);
  const isEliminated = teamState === "ELIMINATED";
  const isChampion = teamState === "CHAMPION";
  const isTradingClosed = isEliminated || isChampion;
  const disabledTitle = isChampion ? "Championship winner - trading closed" : "Team eliminated";

  // Click on BID side (green/left) = open BUY order
  const handleBidSideClick = () => {
    if (!onSelectOrder || isTradingClosed) return;
    onSelectOrder({ teamCode, side: "BUY", price: topBidPrice ?? undefined });
  };

  // Click on ASK side (red/right) = open SELL order
  const handleAskSideClick = () => {
    if (!onSelectOrder || isTradingClosed) return;
    onSelectOrder({ teamCode, side: "SELL", price: topAskPrice ?? undefined });
  };

  const topRow: OrderBookDisplayRow[] = [
    {
      key: "top-book",
      bid: {
        trader: bidTraderDisplay,
        qty: bid.quantityDisplay,
        price: topBidPrice?.toFixed(0) ?? "—",
        hasValue: bid.hasValue,
      },
      ask: {
        trader: askTraderDisplay,
        qty: ask.quantityDisplay,
        price: topAskPrice?.toFixed(0) ?? "—",
        hasValue: ask.hasValue,
      },
    },
  ];
  return (
    <div
      className={cn(
        "flex w-full justify-end self-stretch",
        variant === "tabletCard" ? "marketTabletQuote md:ml-0 md:w-full" : "md:ml-auto md:w-2/5 lg:w-1/2",
      )}
    >
      <div
        className={cn(
          "w-full border border-border-subtle/30 bg-background-raised/60 text-base text-foreground transition-all duration-200",
          variant === "tabletCard" && "min-h-full bg-background/55",
          isExpanded ? "rounded-t-md border-b-0" : "rounded-md",
          isFlashing && "ring-2 ring-brand",
          isTradingClosed && "opacity-60",
        )}
      >
        <div className={cn("flex w-full items-center justify-center", isExpanded ? "pt-1 pb-0" : "py-1")}>
          <OrderBookRows
            rows={topRow}
            showLabels
            showSideLabelColumns={variant === "tabletCard"}
            bidLabel={t("market.quote.bid")}
            askLabel={t("market.quote.ask")}
            onBidClick={handleBidSideClick}
            onAskClick={handleAskSideClick}
            disabled={isTradingClosed}
            disabledTitle={disabledTitle}
          />
        </div>
      </div>
    </div>
  );
}
